import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { BasicDialogService } from '@principle-theorem/ng-shared';
import {
  IPartner,
  PartnerStatus,
} from '@principle-theorem/principle-core/interfaces';
import { Firestore, snapshot, WithRef } from '@principle-theorem/shared';
import { Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'pr-partner-actions',
  templateUrl: './partner-actions.component.html',
  styleUrls: ['./partner-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatMenuModule, MatIcon, MatButtonModule],
})
export class PartnerActionsComponent {
  partner$ = new ReplaySubject<WithRef<IPartner>>(1);
  isTest$: Observable<boolean>;
  isLive$: Observable<boolean>;
  isInactive$: Observable<boolean>;

  @Input()
  set partner(partner: WithRef<IPartner>) {
    if (partner) {
      this.partner$.next(partner);
    }
  }

  constructor(private _dialog: BasicDialogService) {
    this.isTest$ = this.partner$.pipe(
      map((partner) => partner.status === PartnerStatus.Test)
    );

    this.isLive$ = this.partner$.pipe(
      map((partner) => partner.status === PartnerStatus.Live)
    );

    this.isInactive$ = this.partner$.pipe(
      map((partner) => partner.status === PartnerStatus.Inactive)
    );
  }

  async markLive(): Promise<void> {
    const partner = await snapshot(this.partner$);
    await Firestore.patchDoc(partner.ref, {
      status: PartnerStatus.Live,
    });
  }

  async markTesting(): Promise<void> {
    const partner = await snapshot(this.partner$);
    await Firestore.patchDoc(partner.ref, {
      status: PartnerStatus.Test,
    });
  }

  async markInactive(): Promise<void> {
    const partner = await snapshot(this.partner$);
    await Firestore.patchDoc(partner.ref, {
      status: PartnerStatus.Inactive,
    });
  }

  async editName(): Promise<void> {
    const partner = await snapshot(this.partner$);
    const name = await this._dialog.prompt({
      title: 'Update Partner Name',
      prompt: 'Enter a new name for the Partner',
      defaultValue: partner.name,
      submitLabel: 'Save',
    });

    if (!name) {
      return;
    }

    await Firestore.patchDoc(partner.ref, { name });
  }
}
